import { useEffect, useMemo, useRef, useState } from "react";
import "./chartGuide.css";

// Feed page, all steps, desktop + mobile: always place the bubble below the
// highlighted section's bottom edge, clamped into the viewport, so the
// pointer never crosses/overlaps the highlighted outline. Scoped to this
// file only.
function computeMobileBelowPlacement({ rect, bubbleWidth, bubbleHeight, bottomNavbarHeight }) {
  const margin = 12;
  const gap = 24;
  const maxTop = window.innerHeight - bottomNavbarHeight - bubbleHeight - margin;
  const maxLeft = window.innerWidth - bubbleWidth - margin;

  const top = Math.min(Math.max(rect.bottom + gap, margin), Math.max(maxTop, margin));

  const centeredLeft = rect.left + rect.width / 2 - bubbleWidth / 2;
  const left = Math.min(Math.max(centeredLeft, margin), Math.max(maxLeft, margin));

  const ARROW_SIZE = 20;
  const targetCenter = rect.left + rect.width / 2;
  const rawPercent = ((targetCenter - left) / bubbleWidth) * 100;
  const limit = (ARROW_SIZE / bubbleWidth) * 100 + 4;
  const arrowOffset = `${Math.min(Math.max(rawPercent, limit), 100 - limit)}%`;

  return { top, left, arrow: "top", arrowOffset };
}

const FeedGuideSteps = ({ setShowGuide }) => {
  const guideSteps = useMemo(
    () => [
      {
        id: 1,
        target: "feed-guide-leaderboard",
        text: "Find top traders, explore their profiles, and learn from their track records",
      },
      {
        id: 3,
        target: "feed-guide-league-play",
        text: "Join or create public and private leagues. Compete for prizes and build your Trophy Case without affecting your main QUSHi record.",
      },
      {
        id: 4,
        target: "feed-guide-feed",
        text: "See what traders are thinking, share your own ideas, and join the conversation. You can also earn QUSHi Coins as you engage.",
      },
      {
        id: 5,
        target: "feed-guide-notifications",
        text: "See when the community engages with you through likes, comments, follows, and more.",
      },
      {
        id: 6,
        target: "feed-guide-messages",
        text: "Message other traders directly and keep your conversations here.",
      },
    ],
    []
  );

  const [activeSteps, setActiveSteps] = useState(guideSteps);
  const [stepIndex, setStepIndex] = useState(0);
  const [bubblePosition, setBubblePosition] = useState({ top: -1000, left: -1000 });
  const [arrowPosition, setArrowPosition] = useState("bottom");
  const [arrowOffset, setArrowOffset] = useState("50%");
  const [isPositioned, setIsPositioned] = useState(false);
  const bubbleRef = useRef(null);
  const current = activeSteps[stepIndex];

  useEffect(() => {
    const updateActiveSteps = () => {
      const filteredSteps = guideSteps.filter((step) => {
        const element = document.getElementById(step.target);
        return element && element.offsetParent !== null;
      });

      if (filteredSteps.length) {
        setActiveSteps(filteredSteps);
        setStepIndex((prev) => Math.min(prev, filteredSteps.length - 1));
      } else {
        setActiveSteps(guideSteps);
        setStepIndex((prev) => Math.min(prev, guideSteps.length - 1));
      }
    };

    updateActiveSteps();
    window.addEventListener("resize", updateActiveSteps);
    return () => window.removeEventListener("resize", updateActiveSteps);
  }, [guideSteps]);

  useEffect(() => {
    if (!current) {
      return undefined;
    }

    const highlighted = document.querySelectorAll(".chart-guide-highlight");
    highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));

    const targetElement = document.getElementById(current.target);
    if (targetElement) {
      targetElement.classList.add("chart-guide-highlight");
    }

    return () => {
      const cleanupElement = document.getElementById(current.target);
      if (cleanupElement) {
        cleanupElement.classList.remove("chart-guide-highlight");
      }
    };
  }, [current?.target]);

  useEffect(() => {
    if (!current) {
      return undefined;
    }

    const isMobile = window.innerWidth <= 768;
    const bottomNavbarHeight = isMobile ? 64 : 0;

    const compute = () => {
      const targetElement = document.getElementById(current.target);
      if (!targetElement) {
        setIsPositioned(false);
        return;
      }

      let rect = targetElement.getBoundingClientRect();
      if (current.id === 4) {
        const firstPost = targetElement.firstElementChild;
        if (firstPost) {
          rect = firstPost.getBoundingClientRect();
        }
      }

      const bubbleElement = bubbleRef.current;
      const fallbackWidth = isMobile ? 280 : 320;
      const fallbackHeight = isMobile ? 200 : 180;
      const bubbleWidth = bubbleElement?.offsetWidth || fallbackWidth;
      const bubbleHeight = bubbleElement?.offsetHeight || fallbackHeight;

      const { top, left, arrow, arrowOffset } = computeMobileBelowPlacement({
        rect,
        bubbleWidth,
        bubbleHeight,
        bottomNavbarHeight,
      });

      setBubblePosition({ top, left });
      setArrowPosition(arrow);
      setArrowOffset(arrowOffset);
      setIsPositioned(true);
    };

    const isFeedStepMobile = isMobile && current.id === 4;

    let settleTimer;
    const scrollAndPosition = () => {
      const targetElement = document.getElementById(current.target);
      if (!targetElement) {
        setIsPositioned(false);
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      const comfortablyVisible = isFeedStepMobile
        ? rect.top > 80 && rect.top < window.innerHeight - bottomNavbarHeight - 160
        : rect.top > 80 && rect.bottom < window.innerHeight - bottomNavbarHeight - 120;
      if (!comfortablyVisible) {
        targetElement.scrollIntoView({
          behavior: "auto",
          block: "nearest",
          inline: "nearest",
        });
      }

      compute();
      settleTimer = setTimeout(compute, isMobile ? 150 : 220);
    };

    const timer = setTimeout(scrollAndPosition, 60);
    window.addEventListener("resize", compute, { passive: true });
    if (!isFeedStepMobile) {
      window.addEventListener("scroll", compute, { passive: true });
    }

    return () => {
      clearTimeout(timer);
      clearTimeout(settleTimer);
      window.removeEventListener("resize", compute);
      if (!isFeedStepMobile) {
        window.removeEventListener("scroll", compute);
      }
    };
  }, [current?.target]);

  const handleNext = () => {
    if (stepIndex < activeSteps.length - 1) {
      setIsPositioned(false);
      setBubblePosition({ top: -1000, left: -1000 });
      setStepIndex(stepIndex + 1);
      return;
    }

    setShowGuide(false);
  };

  const handleSkip = () => {
    const highlighted = document.querySelectorAll(".chart-guide-highlight");
    highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));
    setShowGuide(false);
  };

  useEffect(() => {
    return () => {
      const highlighted = document.querySelectorAll(".chart-guide-highlight");
      highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));
    };
  }, []);

  if (!current) {
    return null;
  }

  return (
    <>
      <div className="chart-guide-backdrop" onClick={handleSkip} />

      <div
        ref={bubbleRef}
        className={`chart-guide-bubble dynamic-position arrow-${arrowPosition} step-${stepIndex + 1} feed-guide-bubble`}
        style={{
          top: bubblePosition.top,
          left: bubblePosition.left,
          opacity: isPositioned ? 1 : 0,
          "--arrow-offset": arrowOffset,
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        <div className="guide-step-indicator">
          Step {stepIndex + 1} of {activeSteps.length}
        </div>
        <div className="guide-text">{current.text}</div>

        <div className="guide-buttons">
          <button className="next-btn" onClick={handleNext}>
            {stepIndex === activeSteps.length - 1 ? "Finish" : "Next"}
          </button>
          <button className="skip-btn" onClick={handleSkip}>
            Skip
          </button>
        </div>
      </div>
    </>
  );
};

export default FeedGuideSteps;
